import React, { useState, useEffect, useContext, useRef } from 'react';
import './UserCreationModal.css';
import Modal from '../Modal/Modal';
import TextInput from '../TextInput/TextInput';
import EmailInput from '../EmailInput/EmailInput';
import SelectInput from '../SelectInput/SelectInput';
import PhoneInput from '../PhoneInput/PhoneInput';
import CheckboxInput from '../CheckboxInput/CheckboxInput';
import GroupMultiSelect from '../GroupMultiSelect/GroupMultiSelect';
import SearchSelect from '../SearchSelect/SearchSelect';
import FacilityCard from '../FacilityCard/FacilityCard';
import CorporationCard from '../CorporationCard/CorporationCard';
import userFields from '../../common/userFields';
import ActionButtons from '../ActionButtons/ActionButtons';
import baseApi from '../../common/api/base';
import MIUserCard from '../MIUserCard/MIUserCard';
import SimpleMIUserCard from '../SimpleMIUserCard/SimpleMIUserCard';
import convenienceApi from '../../common/api/convenience';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import { AdminContext } from '../../contexts/AdminContext';
import logComponent from '../../common/logComponent';

export default function UserCreationModal({ adminUser, groups: propsGroups, groupsLoading: propsGroupsLoading, gropusError: propsGroupsError, addUser: addUserProps, hide, isVisible }) {
  const environmentContext = useContext(EnvironmentContext);
  const adminContext = useContext(AdminContext);
  const { hasToken } = environmentContext;
  const { facilities, corporations } = adminContext;

  const [validating, setValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState('initial');
  const [selectedUserType, setSelectedUserType] = useState((adminUser.groupTypeString === 'Family' || adminUser.groupTypeString === 'Corporate') ? adminUser.groupTypeString : '');
  const [selectedFacilityId, setSelectedFacilityId] = useState((adminUser.groupTypeString === 'Facility') ? adminUser.facilityId : null);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [facilitiesError, setFacilitiesError] = useState(false);
  const [corporationsLoading, setCorporationsLoading] = useState(true);
  const [corporationsError, setCorporationsError] = useState(false);
  const [monitoredIndividuals, setMonitoredIndividuals] = useState(null);
  const [monitoredIndividualsLoading, setMonitoredIndividualsLoading] = useState(true);
  const [monitoredIndividualsError, setMonitoredIndividualsError] = useState(false);
  const [groups, setGroups] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(true);
  const [surveysError, setSurveysError] = useState(false);
  const [modalShouldExit, setModalShouldExit] = useState(false);

  const [newUser, setNewUser] = useState({
    legalFirstName: null,
    preferredFirstName: null,
    lastName: null,
    emailAddress: null,
    phoneNumber: null,
    userType: null,
    facility: null,
    corporation: null,
    monitoredIndividualUsers: null,
    accessLevel: null,
    canCorrespond: null,
    canImpersonate: null,
    canViewSurveyResponses: null,
    groups: null,
    checkInSurveyId: null
  });

  const newUserRef = useRef({
    legalFirstName: null,
    preferredFirstName: null,
    lastName: null,
    emailAddress: null,
    phoneNumber: null,
    userType: null,
    facility: null,
    corporation: null,
    monitoredIndividualUsers: null,
    accessLevel: null,
    canCorrespond: null,
    canImpersonate: null,
    canViewSurveyResponses: null,
    groups: null,
    checkInSurveyId: null
  })

  const formIsInvalid = isValid === false;

  const modalRef = useRef(null);
  const firstTabRef = useRef(null);
  const actionButtons = useRef(null);

  if (logComponent('UserCreationModal')) { console.debug('UserCreationModal rendered'); }


  const setNewUserRef = (el) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal setNewUserRef'); }
    newUserRef.current = el;
  }
  const showErrorsRef = useRef(false);

  const setShowErrorsRef = (el) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal setShowErrorsRef'); }
    showErrorsRef.current = el;
    setShowErrors(el);
  }



  useEffect(() => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal onMount'); }
    if (!hasToken) return;
    focusFirstItem();

    if (adminUser.groupTypeString !== 'Family') {
      setGroups(propsGroups);
      setGroupsLoading(propsGroupsLoading);
      setGroupsError(propsGroupsError);
    }

    if (adminUser.groupTypeString === 'Family') {
      let monitoredIndividuals =
        adminUser.monitoredIndividualUsers
          .filter(MIUser => MIUser.isAdmin)
          .map(MIUser => {
            let newMIUser = Object.assign({}, MIUser);

            newMIUser.isAdmin = null;
            newMIUser.selected = false;

            return newMIUser;
          });
      setMonitoredIndividuals(monitoredIndividuals);
      setMonitoredIndividualsLoading(false);
      setMonitoredIndividualsError(false);
    }
  }, [])

  useEffect(() => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal useEffect [newUser?.userType?.value]'); }
    if (!hasToken) return;
    // If user type has changed (for Status or Facility admins)
    if (newUser && newUser.userType) {
      if (adminUser.groupTypeString === 'Status Solutions') {
        switch (newUser.userType?.value) {
          case 'Status Solutions':
            loadGroups();
            break;
          case 'Corporate':
            loadCorporations();
            loadGroups();
            break;
          case 'Facility':
            loadGroups();
            loadFacilities();
            break;
          case 'Family':
            loadFacilities();
            break;
          default:
            break;
        }
      } else if (adminUser.groupTypeString === 'Facility') {
        loadMonitoredIndividuals();
        if (newUser.userType?.value === 'Facility') {
          loadCheckInSurveys();
        }

      }
    }
  }, [newUser?.userType?.value])

  useEffect(() => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal onMount [newUser?.facility]'); }
    if (!hasToken || !newUser?.facility) return;
    // If facility has changed 
    if (adminUser.groupTypeString === 'Status Solutions' &&
      newUser.facility.value &&
      newUser.facility.value.length > 0) {
      if (newUser.userType.value === 'Family') {
        loadMonitoredIndividuals();
      } else {
        loadGroups();
        loadCheckInSurveys();
      }
    }
  }, [newUser?.facility])

  useEffect(() => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal onMount [newUser?.corporation]'); }
    if (!hasToken || !newUser?.corporation) return;
    // If corporation has changed 
    if (adminUser.groupTypeString === 'Status Solutions' &&
      newUser?.corporation?.value &&
      newUser?.corporation?.value.length > 0) {
      loadGroups();
    }
  }, [newUser?.corporation])

  useEffect(() => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal onMount [status]'); }
    if (!hasToken) return;
    if (status === 'failure') {
      modalRef.current.scrollTop = 0;
    }
  }, [status])

  const loadMonitoredIndividuals = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal loadMonitoredIndividuals'); }
    if (!hasToken) return;

    let facilityId;

    // If Admin is Status, fetch FacilityIds from newUser
    // If Admin is Facility, user admin's FacilityId
    if (adminUser.groupTypeString === 'Status Solutions') {
      if (newUser.facility && newUser.facility.value && newUser.facility.value.length > 1) {
        loadMonitoredIndividualsFromMultipleFacilities();
        return;
      } else {
        facilityId = newUser.facility.value[0].facilityId;
      }
    } else if (adminUser.groupTypeString === 'Facility') {
      facilityId = adminUser.facilityId;
    }
    setMonitoredIndividualsLoading(true);
    setMonitoredIndividualsError(false);

    baseApi.Facility.GetAllMonitoredIndividuals(facilityId)
      .then(
        result => {
          let monitoredIndividuals = result.data;
          let monitoredIndividualUsers = monitoredIndividuals.map(monitoredIndividual => {
            return {
              monitoredIndividualId: monitoredIndividual.monitoredIndividualId,
              monitoredIndividual: monitoredIndividual,
              isAdmin: null,
              selected: false
            }
          });

          setMonitoredIndividuals(monitoredIndividualUsers);
          setMonitoredIndividualsLoading(false);
          setMonitoredIndividualsError(false);
        },
        error => {
          setMonitoredIndividualsLoading(false);
          setMonitoredIndividualsError(true);
        }
      );
  }

  const loadMonitoredIndividualsFromMultipleFacilities = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal loadMonitoredIndividualsFromMultipleFacilities'); }
    if (!hasToken) return;

    let facilityIds;

    if (newUser.facility && newUser.facility.value && newUser.facility.value.length > 1) {
      facilityIds = newUser.facility.value.map(facility => facility.facilityId);
    }

    if (!facilityIds || facilityIds.length === 0) return;

    convenienceApi.getMonitoredIndividualUsersFromMultipleFacilities(facilityIds)
      .then(
        monitoredIndividuals => {
          setMonitoredIndividuals(monitoredIndividuals);
          setMonitoredIndividualsLoading(false);
          setMonitoredIndividualsError(false);
        },
        error => {
          setMonitoredIndividualsLoading(false);
          setMonitoredIndividualsError(true);
        }
      );
  }

  const loadGroups = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal loadGroups'); }
    if (!hasToken) return;

    let adminUserType = adminUser.groupTypeString;
    let newUserType;

    switch (adminUserType) {
      case 'Status Solutions':
      case 'Facility':
        newUserType = (newUser.userType ? newUser.userType.value : null)
        break;
      case 'Corporate':
        newUserType = 'Corporate';
        break;
      case 'Family':
        newUserType = 'Family';
        break;
      default:
        break;
    }

    setGroups(null);
    setGroupsLoading(true);
    setGroupsError(false);


    let id = 0;
    if (newUserType) {
      switch (newUserType) {
        case 'Status Solutions':
          id = adminUser.statusSolutionsId; // newUser.StatusSolutions is an array, though here it holds only one item
          if (id) {
            baseApi.StatusSolutions.GetAllGroups(id)
              .then(
                result => {
                  const newGroups = result.data;
                  setGroups(newGroups);
                  setGroupsLoading(false);
                  setGroupsError(false);
                },
                error => {
                  setGroupsLoading(false);
                  setGroupsError(true);
                }
              );
          }
          break;
        case 'Corporate':
          id =
            newUserRef.current.corporation &&
            newUserRef.current.corporation.value &&
            newUserRef.current.corporation.value.length > 0 &&
            newUserRef.current.corporation.value[0].corporationId; // newUser.corporation is an array, though here it holds only one item

          if (id) {
            baseApi.Corporation.GetAllGroups(id)
              .then(
                result => {
                  const newGroups = result.data;
                  setGroups(newGroups);
                  setGroupsLoading(false);
                  setGroupsError(false);
                },
                error => {
                  setGroupsLoading(false);
                  setGroupsError(true);
                }
              );
          }
          break;
        case 'Facility':
          id =
            newUserRef.current.facility &&
            newUserRef.current.facility.value &&
            newUserRef.current.facility.value.length > 0 &&
            newUserRef.current.facility.value[0].facilityId;
          if (id) {
            baseApi.Facility.GetAllGroups(id)
              .then(
                result => {
                  const newGroups = result.data;
                  setGroups(newGroups);
                  setGroupsLoading(false);
                  setGroupsError(false);
                },
                error => {
                  setGroupsLoading(false);
                  setGroupsError(true);
                }
              );
          }
          break;
        case 'Family':
          break;
        default:
          break;
      }
    }

  }

  const loadCheckInSurveys = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal loadCheckInSurveys'); }
    if (!hasToken) return;

    let adminUserType = adminUser.groupTypeString;
    let newUserType;

    switch (adminUserType) {
      case 'Status Solutions':
      case 'Facility':
        newUserType = (newUser.userType ? newUser.userType.value : null)
        break;
      case 'Corporate':
        newUserType = 'Corporate';
        break;
      case 'Family':
        newUserType = 'Family';
        break;
      default:
        break;
    }

    setSurveys(null);
    setSurveysLoading(true);
    setSurveysError(false);


    let id = 0;
    if (newUserType) {
      switch (newUserType) {
        case 'Status Solutions':
          break;
        case 'Corporate':
          break;
        case 'Facility':
          id = adminUser.groupTypeString === 'Facility' ? adminUser.facilityId :
            newUserRef.current.facility &&
            newUserRef.current.facility.value &&
            newUserRef.current.facility.value.length > 0 &&
            newUserRef.current.facility.value[0].facilityId;
          if (id) {
            baseApi.Survey.GetAllCheckInSurveysForFacility(id)
              .then(
                result => {
                  let defaultChoice = [{ key: '', value: '' }];
                  let availableSurveys = result.data.map(survey => {
                    return {
                      key: survey.surveyName + ' (' + survey.ownerName + ')', 
                      value: survey.surveyId
                    };
                  });
                  let newSurveys = defaultChoice.concat(availableSurveys);
                  setSurveys(newSurveys);
                  setSurveysLoading(false);
                  setSurveysError(false);
                },
                error => {
                  setSurveys(null);
                  setSurveysLoading(false);
                  setSurveysError(true);
                }
              );
          }
          break;
        case 'Family':
          break;
        default:
          break;
      }
    }

  }


  const loadFacilities = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal loadFacilities'); }
    if (!hasToken) return;

    if (adminContext.isFresh('facilities')) {
      setFacilitiesLoading(false);
      setFacilitiesError(false);
      return;
    }

    setFacilitiesLoading(true);
    setFacilitiesError(false);

    baseApi.Facility.GetAll()
      .then(
        result => {
          const newFacilities = result.data;
          adminContext.setFacilities(newFacilities);

          setFacilitiesLoading(false);
          setFacilitiesError(false);
        },
        error => {
          setFacilitiesLoading(false);
          setFacilitiesError(true);
        }
      );
  }

  const loadCorporations = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal loadCorporations'); }
    if (!hasToken) return;

    if (adminContext.isFresh('corporations')) {
      setCorporationsLoading(false);
      setCorporationsError(false);
      return;
    }

    setCorporationsLoading(true);
    setCorporationsError(false);

    baseApi.Corporation.GetAll()
      .then(
        result => {
          const newCorporations = result.data;
          adminContext.setCorporations(newCorporations);
          setCorporationsLoading(false);
          setCorporationsError(false);
        },
        error => {
          setCorporationsLoading(false);
          setCorporationsError(true);
        }
      )
  }

  const showValidationErrors = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal showValidationErrors'); }
    setShowErrorsRef(true);
  }

  const hideValidationErrors = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal hideValidationErrors'); }
    setShowErrorsRef(false);
  }

  const validateField = (field) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal validateField'); }
    if (field.name === 'monitoredIndividualUsers' && field.value) {
      handleMonitoredIndividualUpdate(field.value);
    }

    let newUserThis = { ...newUserRef.current };
    newUserThis[field.name] = field;
    setNewUserRef(newUserThis);
    setNewUser(newUserThis); //doing this as both ref and state so it will have the values updated when I need but still refresh the page when needed

  }

  const handleMonitoredIndividualUpdate = (selectedMonitoredIndividuals) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal handleMonitoredIndividualUpdate'); }

    if (!monitoredIndividuals) return;

    // go through each monitored individual and set Selected prop
    let updatedMonitoredIndividuals = monitoredIndividuals.map(monitoredIndividual => {
      if (selectedMonitoredIndividuals.some(selectedMonitoredIndividual => monitoredIndividual.monitoredIndividualId === selectedMonitoredIndividual.monitoredIndividualId)) {
        monitoredIndividual.selected = true;
      } else {
        monitoredIndividual.selected = false;
        monitoredIndividual.isAdmin = null;
        monitoredIndividual.needsAccessLevel = false;
      }

      return monitoredIndividual;
    });
    setMonitoredIndividuals(updatedMonitoredIndividuals);
  }

  const submit = (e) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal submit'); }
    setTimeout(() => {
      // check all necessary fields for adminUser type and newUser type; if there's no new UserType selected
      let requiredFieldList = getRequiredFieldList();

      if (!requiredFieldList) {
        handleInvalidSubmission();
        return;
      }


      let isValidThis = requiredFieldList.reduce((prev, curr) => prev && newUser[curr] && newUser[curr].isValid, true);

      // If there's any item in newUser.monitoredIndividualUsers whose IsAdmin key is null
      if (newUser.monitoredIndividualUsers &&
        newUser.monitoredIndividualUsers.value.length > 0 &&
        newUser.monitoredIndividualUsers.value.some(monitoredIndividual => monitoredIndividual.isAdmin == null)) {
        isValidThis = false;
      }

      if (!isValidThis) {
        handleInvalidSubmission();
      } else {
        handleValidSubmission();
      }
    }, 1000);
  }

  const handleValidSubmission = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal handleValidSubmission'); }
    hideValidationErrors();
    addUser();
    setIsValid(true);
  }

  const handleInvalidSubmission = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal handleInvalidSubmission'); }

    if (newUser.monitoredIndividualUsers && newUser.monitoredIndividualUsers.value.length === 0) {

      let newMonitoredIndividuals = monitoredIndividuals.map(monitoredIndividual => {
        if (newUser.monitoredIndividualUsers.value.some(MIUser => {
          return MIUser.isAdmin === null &&
            MIUser.monitoredIndividualId === monitoredIndividual.monitoredIndividualId;
        })) {
          monitoredIndividual.needsAccessLevel = true;
        } else {
          monitoredIndividual.needsAccessLevel = false;
        }

        return monitoredIndividual;
      });
      setMonitoredIndividuals(newMonitoredIndividuals);
    }

    showValidationErrors();
    modalRef.current.scrollTop = 0;
    focusFirstItem();

    setIsValid(false);

    reset(); // necessary? 
  }

  const getRequiredFieldList = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal getRequiredFieldList'); }
    let adminUserType = adminUser.groupTypeString;
    let newUserType;

    switch (adminUserType) {
      case 'Status Solutions':
      case 'Facility':
        newUserType = (newUser.userType ? newUser.userType.value : null)
        break;
      case 'Corporate':
        newUserType = 'Corporate';
        break;
      case 'Family':
        newUserType = 'Family';
        break;
      default:
        break;
    }

    if (userFields[adminUserType] && userFields[adminUserType][newUserType]) {
      return userFields[adminUserType][newUserType];
    } else {
      return null;
    }
  }

  const addUser = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal addUser'); }

    let userToSubmit = {};

    setStatus('pending');

    let newUserEntries = Object.entries(newUser);

    newUserEntries.forEach(entry => {
      userToSubmit[entry[0]] = entry[1] ? entry[1].value : null;
    })

    // Add some fields & modify other fields that require it
    userToSubmit.status = "Active";
    userToSubmit.photoPath = "";
    userToSubmit.canCorrespond = userToSubmit.canCorrespond == null ? false : userToSubmit.canCorrespond;
    userToSubmit.canImpersonate = userToSubmit.canImpersonate == null ? false : userToSubmit.canImpersonate;
    userToSubmit.canViewSurveyResponses = userToSubmit.canViewSurveyResponses == null ? false : userToSubmit.canViewSurveyResponses;

    // NOTE: This will change depending on which user types can create users at 'lower' levels
    let userType = userToSubmit.userType ? userToSubmit.userType : null;

    if (!userType) {
      if (adminUser.groupTypeString === 'Corporate') {
        userType = 'Corporate';
      } else if (adminUser.groupTypeString === 'Family') {
        userType = 'Family';
      }
    }

    switch (userType) {
      case 'Status Solutions':
        userToSubmit.statusSolutionsId = 1;
        userToSubmit.isAdmin = userToSubmit.accessLevel === 'Admin';
        break;
      case 'Corporate':
        userToSubmit.corporationId = (adminUser.corporationId ? adminUser.corporationId : userToSubmit.corporation[0].corporationId);
        userToSubmit.isAdmin = userToSubmit.accessLevel === 'Admin';
        break;
      case 'Facility':
        userToSubmit.facilityId = (adminUser.facilityId ? adminUser.facilityId : userToSubmit.facility[0].facilityId);
        userToSubmit.isAdmin = userToSubmit.accessLevel === 'Admin';
        break;
      case 'Family':
        break;
      default:
        break;
    }

    // This may not be necessary, but deleting these keys anyway
    delete userToSubmit.accessLevel;
    delete userToSubmit.userType;
    delete userToSubmit.facility;
    delete userToSubmit.corporation;

    convenienceApi.addNewUser(userToSubmit)
      .then(
        user => {
          setStatus('success');
          addUserProps(user);
          startModalExit();
        },
        error => {
          setStatus('failure');
        }
      )
  }

  const reset = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal reset'); }
    setStatus('initial');
  }

  const focusFirstItem = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal focusFirstItem'); }
    if (firstTabRef && firstTabRef.current) firstTabRef.current.focus();
  }

  const handleShiftTab = (e) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal handleShiftTab'); }
    const { key, shiftKey } = e;

    if (key !== 'Tab' || !shiftKey) return;

    e.preventDefault();
    if (actionButtons && actionButtons.current) actionButtons.current.focus();
  }

  const getUserTypeArray = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal getUserTypeArray'); }

    switch (adminUser.groupTypeString) {
      case 'Status Solutions':
        return ['', 'Status Solutions', 'Corporate', 'Facility', 'Family'];
      case 'Corporate':
        return ['Corporate'];
      case 'Facility':
        return ['', 'Facility', 'Family'];
      case 'Family':
        return ['Family'];
      default:
        break;
    }
  }

  const startModalExit = () => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal startModalExit'); }
    setModalShouldExit(true);
  }

  const handleKeyDown = (e) => {
    if (logComponent('UserCreationModal')) { console.debug('UserCreationModal handleKeyDown'); }
    if (e.key === 'Escape') startModalExit();
  }
  if (groups) {
    if (logComponent('UserCreationModal')) {
      console.debug('UserCreationModal render:   Groups-count:' + groups.length);
    }
  } else {
    if (logComponent('UserCreationModal')) {
      console.debug('UserCreationModal render');
    }
  }


  return (
    <Modal isVisible={isVisible} shouldExit={modalShouldExit} hide={hide} ref={modalRef} remainOnBackdropClick={true}>
      <div
        className="User-Creation-Modal"
        onKeyDown={handleKeyDown}>
        <h3 className="User-Creation-Modal__heading">Create new user</h3>
        <p className={'User-Creation-Modal__page-error' + (showErrors ? ' visible' : '')}>There were some errors. Please double check the information and resubmit.</p>
        <p className={'User-Creation-Modal__page-error' + (status === 'failure' ? ' visible' : '')}>Sorry, we were unable to complete the action.  User was not created.  Please try again.</p>
        <form className="User-Creation-Modal__form">
          <TextInput
            label="First name (legal)"
            name="legalFirstName"
            rules={{ required: true }}
            errorMessages={{ required: 'Please provide the user\'s legal first name' }}
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField}
            ref={firstTabRef}
            onKeyDown={handleShiftTab}
          />
          <TextInput
            label="First name (preferred)"
            name="preferredFirstName"
            instructions="If applicable"
            validate={validating}
            onValidate={validateField} />
          <TextInput
            label="Last name"
            name="lastName"
            rules={{ required: true }}
            errorMessages={{ required: 'Please provide the user\'s last name' }}
            showError={showErrorsRef.current}
            validate={validating}
            onValidate={validateField}
            requireEmailUniqueness={true} />
          <EmailInput
            label="Email address"
            name="emailAddress"
            rules={{ required: true }}
            errorMessages={[
              {
                rules: ['required', 'validEmail'],
                text: 'Please provide a valid email address for the user'
              },
              {
                rules: ['unusedEmail'],
                text: 'This email address is already in use'
              }
            ]}
            showError={showErrorsRef.current}
            validate={validating}
            onValidate={validateField} />
          <PhoneInput
            label="Phone number"
            name="phoneNumber"
            rules={{ maxLength: 10, required: true }}
            errorMessage="Please provide a valid phone number for the user"
            showError={formIsInvalid}
            validate={validating}
            onValidate={validateField} />
          {
            adminUser.groupTypeString !== 'Family' &&
            <SelectInput
              label="User type"
              name="userType"
              items={getUserTypeArray()}
              errorMessage="Please select a user type"
              initialValue={selectedUserType}
              showError={formIsInvalid}
              rules={{ required: true }}
              validate={validating}
              onValidate={validateField}
            />
          }
          {
            adminUser.groupTypeString === 'Status Solutions' &&
            newUser.userType &&
            (newUser.userType.value === 'Family' || newUser.userType.value === 'Facility') &&
            <SearchSelect
              label="Select facility"
              selectionMode="search"
              maximumSelections={1}
              itemKeyField="facilityId"
              itemPluralTerm="facilities"
              items={facilities}
              itemsLoading={facilitiesLoading}
              itemsError={facilitiesError}
              itemComponent={FacilityCard}
              itemComponentPropName="facility"
              searchFieldGroups={[
                ['name'],
                ['salesforceSiteId']
              ]}
              name="facility"
              rules={(newUser.userType.value === 'Family' ? { minItems: 1, required: true } : { numItems: 1, required: true })}
              showError={showErrorsRef.current}
              errorMessage="Please select a facility"
              validate={validating}
              onValidate={validateField}
              instructions="Only one facility can be selected."
            />
          }
          {
            adminUser.groupTypeString === 'Status Solutions' &&
            newUser.userType &&
            newUser.userType.value === 'Corporate' &&
            <SearchSelect
              label="Select corporation"
              selectionMode="search"
              maximumSelections={1}
              itemKeyField="corporationId"
              itemPluralTerm="corporations"
              items={corporations}
              itemsError={corporationsError}
              itemsLoading={corporationsLoading}
              itemComponent={CorporationCard}
              itemComponentPropName="corporation"
              searchFieldGroups={[
                ['name']
              ]}
              name="corporation"
              rules={{
                numItems: 1, required: true
              }}
              showError={formIsInvalid}
              errorMessage="Please select a corporation"
              validate={validating}
              onValidate={validateField}
              instructions="Only one corporation can be selected." />
          }
          {
            (adminUser.groupTypeString === 'Family' ||
              (adminUser.groupTypeString === 'Facility' && newUser.userType && newUser.userType.value === 'Family') ||
              (adminUser.groupTypeString === 'Status Solutions' && newUser.userType && newUser.userType.value === 'Family' && newUser.facility && newUser.facility.value && newUser.facility.value.length > 0)) &&
            <SearchSelect
              selectionMode={adminUser.groupTypeString === 'Family' && adminUser.monitoredIndividualUsers.length <= 5 ? 'list' : 'search'}
              label="Select individual(s)"
              itemKeyField="monitoredIndividualId"
              itemPluralTerm="individuals"
              items={monitoredIndividuals}
              itemsLoading={monitoredIndividualsLoading}
              itemsError={monitoredIndividualsError}
              potentialItemComponent={SimpleMIUserCard}
              selectedItemComponent={MIUserCard}
              itemComponentPropName="miUser"
              searchFieldGroups={[
                ['monitoredIndividual.legalFirstName', 'monitoredIndividual.lastName'],
                ['monitoredIndividual.preferredFirstName', 'monitoredIndividual.lastName'],
                ['monitoredIndividual.dateOfBirth']
              ]}
              name="monitoredIndividualUsers"
              rules={{
                minItems: 1, required: true
              }}
              showError={showErrorsRef.current}
              errorMessage="Please select at least one individual"
              validate={validating}
              onValidate={validateField}
            />
          }
          {
            newUser.userType &&
            newUser.userType.value !== 'Family' &&
            <SelectInput
              label="Access level"
              name="accessLevel"
              items={['', 'Admin', 'Standard']}
              rules={{ required: true }}
              errorMessage="Please select an access level"
              showError={formIsInvalid}
              validate={validating}
              onValidate={validateField} />
          }
          {
            newUser.userType &&
            newUser.userType.value === 'Facility' &&
            <CheckboxInput
              label="Correspondence override"
              name="canCorrespond"
              instructions="Note: when checked, this user will always be able to correspond with family members, regardless of permissions based on group membership."
              explanation="This user may correspond with individuals' family."
              initialValue={false}
              validate={validating}
              onValidate={validateField} />
          }
          {
            newUser.userType &&
            (newUser.userType.value === 'Status Solutions' || newUser.userType.value === 'Corporate') &&
            <CheckboxInput
              label="Mimic override"
              name="canImpersonate"
              instructions="Note: when checked, this user will always be able to mimic other users in the MIMI system associated with their organization to assist with troubleshooting or other needs."
              explanation="This user may mimic other users in MIMI."
              initialValue={false}
              validate={validating}
              onValidate={validateField} />
          }
          {
            newUser.userType &&
            newUser.userType.value === 'Facility' &&
            <CheckboxInput
              label="View survey responses override"
              name="canViewSurveyResponses"
              instructions="Note: when checked, this user will always be able view survey responses in the MIMI system, regardless of permissions based on group membership."
              explanation="This user may view survey responses in MIMI."
              initialValue={false}
              validate={validating}
              onValidate={validateField} />
          }
          {
            newUser.userType &&
            newUser.userType.value === 'Facility' && 
            <SelectInput
              label="Check-In survey"
              name="checkInSurveyId"
              items={surveys}
              errorMessage="Please select a check-in survey for this user.  If not is selected, it will use the default survey"
              showError={formIsInvalid}
              rules={null}
              validate={validating}
              onValidate={validateField}
            />
          }
          {
            newUser.userType &&
            newUser.userType.value !== 'Family' &&
            (
              (((adminUser.groupTypeString === 'Status Solutions') && (newUser.facility && newUser.facility.value && newUser.facility.value.length > 0 && newUser.facility.value[0].facilityId > 0)) || adminUser.groupTypeString === 'Facility') ||
              (((adminUser.groupTypeString === 'Status Solutions') && (newUser.corporation && newUser.corporation.value && newUser.corporation.value.length > 0 && newUser.corporation.value[0].corporationId > 0)) || adminUser.groupTypeString === 'Corporate') ||
              (adminUser.groupTypeString === 'Status Solutions' && newUser.userType.value === 'Status Solutions')

            ) &&
            <GroupMultiSelect
              label="Group memberships"
              name="groups"
              allGroups={groups}
              groupsLoading={groupsLoading}
              groupsError={groupsError}
              reloadGroups={loadGroups}
              validate={validating}
              onValidate={validateField} />
          }
          <ActionButtons
            mainAction={submit}
            mainActionText="Create user"
            cancel={startModalExit}
            cancelText="Cancel"
            currentStatus={status}
            ref={actionButtons} />

          <span
            tabIndex="0"
            className="User-Creation-Modal__focus-control"
            onFocus={focusFirstItem}></span>
        </form>
      </div>
    </Modal>
  );
}
